/**
 * The audit actor filter (F-62): a dropdown of the firm's members (name + role) plus
 * "System" (worker-originated events carry no actor) and "Any actor". The value is the
 * actor user id (the `user` search param / `user_id` query param), `''` for any actor and
 * `system` for events with no actor.
 *
 * Members come from the team query the app already has: `GET /users` for `users.read`
 * holders, else `GET /users/assignable` for `cases.assign` holders. A role holding neither
 * still gets every actor seen in the loaded events, so the control is never empty-handed.
 */
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { Select } from '../ui/index.ts';
import { api, type AuditEventWire } from '../../lib/api.ts';
import { formatRole } from '../../lib/format/role.ts';
import { usePermission } from '../../lib/permissions/index.tsx';

/** The `user_id` value that selects events with no actor (worker / system originated). */
export const SYSTEM_ACTOR = 'system';

interface Member {
  user_id: string;
  full_name: string;
  role: string | null;
}

function useMembers(): Member[] {
  const canReadUsers = usePermission('users.read');
  const canAssign = usePermission('cases.assign');
  const query = useQuery<Member[]>({
    queryKey: ['audit', 'actor-members', canReadUsers ? 'users' : 'assignable'],
    queryFn: () => (canReadUsers ? api.users.list() : api.users.assignable()),
    enabled: canReadUsers || canAssign,
    staleTime: 5 * 60_000,
  });
  return query.data ?? [];
}

export function ActorSelect({
  value,
  onChange,
  seenEvents,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Events already loaded: any actor in them missing from the member list is added by name. */
  seenEvents: AuditEventWire[];
}): JSX.Element {
  const members = useMembers();
  const options = new Map<string, string>();
  for (const m of members) {
    options.set(m.user_id, m.role ? `${m.full_name} (${formatRole(m.role)})` : m.full_name);
  }
  for (const e of seenEvents) {
    if (e.actor_user_id !== null && !options.has(e.actor_user_id)) {
      options.set(e.actor_user_id, e.actor_name ?? e.actor_user_id);
    }
  }
  // A bookmarked actor not (yet) in the list stays selectable rather than silently resetting.
  if (value !== '' && value !== SYSTEM_ACTOR && !options.has(value)) options.set(value, value);

  return (
    <Select
      label="Actor"
      aria-label="Filter by actor"
      fieldSize="sm"
      className="argus-audit-filters__text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Any actor</option>
      <option value={SYSTEM_ACTOR}>System</option>
      {[...options.entries()].map(([id, label]) => (
        <option key={id} value={id}>
          {label}
        </option>
      ))}
    </Select>
  );
}
